/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, October 31st 2022, 2:26:51 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useGlobalScope } from "core/providers";
import { useRemoteDatasetsAPI } from "datasets/services/RemoteDatasetsAPI";
import { AjaxSource, SOURCE_REQUEST_METHODS, SOURCE_RESOURCE_TYPES } from "lib/schemas";
import { getDefaultAjaxSource } from "lib/factories";
import { PrimaryKeyValue } from "lib/types";
import {
  Responsive2ColGridContainer as GridContainer,
  Responsive2ColGridItem as GridItem,
  TextFormInput,
  DropDownFormInput,
} from "ui-widgets";
import { RelatedObjectOSAPIFormProps, OSAPIForm } from "ui-widgets/forms";
import { AjaxSourceParameterNestedPopUpField } from "./AjaxSourceParameter";

interface AjaxSourceFormProps extends RelatedObjectOSAPIFormProps<AjaxSource> {
  ajaxSourceID?: PrimaryKeyValue<AjaxSource>;
}

export const AjaxSourceForm = (props: AjaxSourceFormProps) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const { useAjaxSourceAPIForm } = useRemoteDatasetsAPI();

  const useAPIFormReturn = useAjaxSourceAPIForm({
    ajaxSourceId: props.ajaxSourceID,
    ajaxSourceDefault: getDefaultAjaxSource(selectedOrganization?.id),
    updateAjaxSourceFromAPICallback: () => {},
    onSaveAjaxSource: props.onSuccessfulAPISave,
  });

  const {
    useFormReturn: { watch },
  } = useAPIFormReturn;

  const watchedAjaxSource = watch();

  return (
    <>
      <OSAPIForm useAPIFormReturn={useAPIFormReturn} onFormCancel={props.onFormCancel} loaderType="form-skeleton">
        <GridContainer>
          <GridItem>
            <TextFormInput<AjaxSource> label="Name" schema_field_key="name" />
          </GridItem>
          <GridItem>
            <TextFormInput<AjaxSource> label="Remote Source URL" schema_field_key="source" />
          </GridItem>
          <GridItem>
            <DropDownFormInput<AjaxSource>
              schema_field_key="request_method"
              label="Request Method"
              options={SOURCE_REQUEST_METHODS}
            />
          </GridItem>
          <GridItem>
            <DropDownFormInput<AjaxSource>
              schema_field_key="resource_type"
              label="Resource Type"
              options={SOURCE_RESOURCE_TYPES}
            />
          </GridItem>
          <GridItem>
            <TextFormInput<AjaxSource> label="Soap Call" schema_field_key="soap_call" />
          </GridItem>
        </GridContainer>
        <AjaxSourceParameterNestedPopUpField
          schema_field_key="source_parameters"
          ajaxSource={watchedAjaxSource}
          label="Remote Data Source Input"
        />
      </OSAPIForm>
    </>
  );
};
